/** @module @ignore */
import * as React from 'react';
import { FeatureCollection } from 'geojson';
import { Layer, MapMouseEvent, Source, useMap } from 'react-map-gl/mapbox';
import { polygon, featureCollection } from '@turf/helpers';
import booleanIntersects from '@turf/boolean-intersects';

import { Mouse } from '@longline/aqua-ui/controls/Mouse';
import { Key } from '@longline/aqua-ui/controls/Key';

import { POLYGON_FILL_COLOR_VALID, POLYGON_LINE_COLOR_VALID } from './EditorConfig';
import { IRect } from '../types/IRect';
import { IStructure } from '../types/IStructure';
import { PolygonUtils } from '../util/PolygonUtils';
import { useGlobalState } from '../contexts/state/GlobalStateContext';
import { Guide } from '../ui/Guide';

interface IProps {
  /** Fired when box select is cancelled. */
  onCancel: () => void;
  /** Fired when box select completes with selected structures. */
  onComplete: (structures: IStructure[]) => void;
}

/**
 * Box select builder component.
 * Allows user to draw a rectangle on the map to select multiple structures.
 */
const BoxSelectBuilder = (props: IProps) => {
  const map = useMap().current!;
  const globalState = useGlobalState();
  const rect = React.useRef<IRect | null>(null);
  const [time, setTime] = React.useState<number | null>(null);

  const handleKeydown = (e: KeyboardEvent) => {
    switch(e.code) {
      case 'Enter':
      case 'NumpadEnter':
        submit();
        break;
      case 'Escape':
        props.onCancel();
        break;
    }
  };

  const handleMouseMove = (e: MapMouseEvent) => {
    if(rect.current === null) return;
    rect.current = {
      ...rect.current,
      maxLng: e.lngLat.lng,
      maxLat: e.lngLat.lat,
    };
    setTime(Math.random());
  };

  const handleClick = (e: MapMouseEvent) => {
    e.preventDefault();

    if(rect.current === null) {
      // First click: set the first corner
      rect.current = {
        minLng: e.lngLat.lng,
        maxLng: e.lngLat.lng,
        minLat: e.lngLat.lat,
        maxLat: e.lngLat.lat,
      };
      setTime(Math.random());
    } else {
      // Second click: complete the selection
      submit();
    }
  };

  const submit = () => {
    if(rect.current == null) {
      props.onCancel();
      return;
    }

    // Normalize rect coordinates
    const r = {...rect.current};
    if(r.minLng > r.maxLng) {
      const temp = r.minLng;
      r.minLng = r.maxLng;
      r.maxLng = temp;
    }
    if(r.minLat > r.maxLat) {
      const temp = r.minLat;
      r.minLat = r.maxLat;
      r.maxLat = temp;
    }

    // Create selection box polygon
    const boxPoly = polygon([[
      [r.minLng, r.minLat],
      [r.maxLng, r.minLat],
      [r.maxLng, r.maxLat],
      [r.minLng, r.maxLat],
      [r.minLng, r.minLat]
    ]]);

    // Find all structures that intersect with the selection box
    const selectedStructures = globalState.structures.filter(structure => {
      let structurePoly;
      if (structure.type === 'circle') {
        structurePoly = PolygonUtils.getCirclePolygon(structure.point, structure.radius);
      } else {
        const pts = structure.points.map(p => [p.lng, p.lat]);
        pts.push(pts[0]); // Close the polygon
        structurePoly = polygon([pts]);
      }
      return booleanIntersects(boxPoly, structurePoly);
    });

    props.onComplete(selectedStructures);
  };

  React.useEffect(() => {
    if (!map) return;

    document.addEventListener('keydown', handleKeydown);
    map.on('click', handleClick);
    map.on('mousemove', handleMouseMove);
    map.getCanvas().style.cursor = 'crosshair';

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      map.off('click', handleClick);
      map.off('mousemove', handleMouseMove);
      map.getCanvas().style.cursor = '';
    };
  }, [map]);

  const getRectJSON = (): FeatureCollection | null => {
    if(!rect.current) return null;

    const poly = polygon([[
      [rect.current.minLng, rect.current.minLat],
      [rect.current.maxLng, rect.current.minLat],
      [rect.current.maxLng, rect.current.maxLat],
      [rect.current.minLng, rect.current.maxLat],
      [rect.current.minLng, rect.current.minLat]
    ]]);

    return featureCollection([poly]);
  };

  return (
    <>
      <Guide x={16} y={64} title="Box select structures">
        Move the mouse to size the box, then click <Mouse size={14} left/> to select structures. <Key value="ESC"/> cancels.
      </Guide>
      <Source type="geojson" data={getRectJSON() ?? undefined}>
        <Layer
          type="fill"
          paint={{
            "fill-color": POLYGON_FILL_COLOR_VALID,
            "fill-opacity": 0.2
          }}
        />
        <Layer
          type="line"
          paint={{
            "line-color": POLYGON_LINE_COLOR_VALID,
            "line-width": 1.5,
            "line-dasharray": [ 2, 1 ]
          }}
        />
      </Source>
    </>
  );
};

export { BoxSelectBuilder };
